/* eslint-disable @typescript-eslint/require-await */
import { FC, useContext, useEffect, useState } from 'react'
import { EventBus } from 'shared-code/event-bus'
import { BasicStyledComponent } from 'shared-definitions/types'
import { CommentCountsContext } from '../contexts/CommentCountsContext'
import { CommentHubAPIContext } from '../contexts/CommentHubAPIContext'
import { useAbortableEffect } from '../hooks/use-abortable-effect'
import { logError } from 'shared-code/log'

interface CommentHubCommentsCounterProps extends BasicStyledComponent {
  containerId: string
  /** Draw nothing while the count is unknown or zero — never a "0" (the article byline, whose
   *  empty state invites the first comment instead). Default: the number, "0" included. */
  hideZero?: boolean
}

// comments cache
const commentsCounts: Map<string, number> = new Map()
const commentsIdsToUpdate = new Set<string>()
const eb = new EventBus<'updated'>()
let timer: ReturnType<typeof setTimeout> | null = null

const CommentHubCommentsCounter: FC<CommentHubCommentsCounterProps> = ({
  className,
  containerId,
  hideZero = false,
}) => {
  // The server-fetched count (`CommentCountsContext`, from the page props) is the first render on
  // both sides — the number is in the HTML and hydrates as is; the batched fetch below still
  // refreshes it (an ISR page is up to `pageRevalidateDelay` seconds old). Without a seed, 0.
  const seed = useContext(CommentCountsContext)[containerId]
  const [counter, setCounter] = useState(seed ?? 0)
  const { api } = CommentHubAPIContext.useContainer()

  useEffect(() => {
    const handler = (): void => {
      if (commentsCounts.has(containerId)) {
        setCounter(commentsCounts.get(containerId)!)
      }
    }
    eb.on('updated', handler)

    return () => {
      eb.off('updated', handler)
    }
  }, [containerId])

  useAbortableEffect(
    async signal => {
      if (!api) {
        return
      }

      if (commentsCounts.has(containerId)) {
        setCounter(commentsCounts.get(containerId)!)
        return
      }

      commentsIdsToUpdate.add(containerId)

      if (timer) {
        clearTimeout(timer)
      }

      timer = setTimeout(() => {
        void api
          .getCommentsCount(signal, Array.from(commentsIdsToUpdate))
          .then(comments => {
            comments.forEach(comment => {
              commentsCounts.set(comment.containerId, comment.commentCount)
            })
            commentsIdsToUpdate.clear()
            eb.emit('updated')
          })
          .catch(e => {
            // The debounced batch rides the LAST scheduler's signal, and that counter unmounting
            // (a rail card swapped, a panel closed) aborts the whole batch. Without this catch the
            // abort was an unhandled rejection — the dev overlay's "signal is aborted without
            // reason". The ids stay queued, so the next counter's schedule picks them up; only a
            // real failure is logged.
            if (!signal.aborted) {
              logError('CommentHubCommentsCounter: count fetch failed', e)
            }
          })
      }, 500)
    },
    [containerId]
  )

  return <div className={className}>{hideZero && counter === 0 ? null : counter}</div>
}

export default CommentHubCommentsCounter
